import {
  Box,
  formatMoney,
  Icon,
  PressableOverlay,
  Text,
} from '@betty/moonlight';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from '@betty/i18n';
import { formatDateWithTime } from '@betty/toolkit/utils';
import { BottomSheet } from '@betty/navigation';
import { TransactionListItemItem } from '../../state/transactionHistory/transactionHistory.types';
import { useDispatch } from '@betty/toolkit/state';
import { fetchTransactionHistory } from '../../state';

function TransactionHistoryListItem({
  selectedDate,
  amount,
  createdAt,
  transactionId,
  paymentMethodVendor,
  paymentMethodType,
  paymentMethodDetails,
  status,
  transactionType,
  reason,
  isCancelable,
  isWithdrawalConfirmationRequired,
}: TransactionListItemItem) {
  const dispatch = useDispatch();

  const [t] = useTranslation('account');
  const date = useMemo(() => formatDateWithTime(createdAt), [createdAt]);
  const paymentMethodInfo = useMemo(() => {
    return [paymentMethodType, paymentMethodVendor, paymentMethodDetails]
      .filter(Boolean)
      .join(' | ');
  }, [paymentMethodType, paymentMethodVendor, paymentMethodDetails]);

  const formattedAmount = useMemo(() => {
    const money = formatMoney(amount);

    switch (transactionType) {
      case 'Withdrawal':
        return `-${money}`;
      case 'Deposit':
        return `+${money}`;
      case 'Manual Adjustment':
        return amount < 0 ? money : `+${money}`;
      default:
        return money;
    }
  }, [amount, transactionType]);

  const cancelWithdrawal = useCallback(() => {
    BottomSheet.open({
      namespace: 'account',
      type: 'CancelWithdrawal',
      titleNS: 'account',
      titleKey: 'cancelWithdrawalTitle',
      data: {
        amount: formattedAmount,
        transactionId,
        paymentMethodInfo: paymentMethodInfo,
      },
    });
  }, [formattedAmount, transactionId, paymentMethodInfo]);

  const confirmWithdrawal = useCallback(() => {
    BottomSheet.open({
      namespace: 'wallet',
      type: 'WithdrawConfirmation',
      closeOnBlur: false,
      showCloseIcon: false,
      enablePanDownToClose: false,
      data: {
        transactionId,
      },
      onClose: () => {
        dispatch(
          fetchTransactionHistory({ startDate: selectedDate, replace: true }),
        );
      },
    });
  }, [dispatch, selectedDate, transactionId]);

  return (
    <Box gap="space-300">
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text variant="body-m" color="darkSurfacePrimary">
          {date}
        </Text>
        <Text variant="body-s" color="darkSurfaceMuted">
          {t('transactionHistoryPaymentId', { paymentId: transactionId })}
        </Text>
      </Box>
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text variant="body-m" gradient="darkSurfaceAccent">
          {t(transactionType.replace(/ /g, '_').toLowerCase())}
        </Text>
        <Text variant="h6" color="darkSurfacePrimary">
          {formattedAmount}
        </Text>
      </Box>
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text variant="body-s" color="darkSurfaceMuted">
          {paymentMethodInfo}
        </Text>
        <Text variant="body-s" color="darkSurfaceMuted">
          {t(status.toLowerCase())}
        </Text>
      </Box>
      <Box flexDirection="row" justifyContent="flex-end" alignItems="center">
        {reason ? (
          <Text variant="body-s" color="darkSurfaceMuted">
            {reason}
          </Text>
        ) : null}
      </Box>

      {isWithdrawalConfirmationRequired && status !== 'Canceled' ? (
        <Box
          flexDirection="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Box flexDirection="row" alignItems="center" gap="space-200">
            <Icon
              name="info"
              width="space-600"
              height="space-600"
              gradient="accentBackground"
            />
            <Text
              variant="body-m"
              gradient="darkSurfaceAccent"
              locNS="account"
              locKey="confirmationRequired"
            />
            <PressableOverlay onPress={confirmWithdrawal} />
          </Box>
        </Box>
      ) : null}

      {isCancelable ? (
        <Box
          flexDirection="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Box flexDirection="row" alignItems="center" gap="space-200">
            <Icon
              name="info"
              width="space-600"
              height="space-600"
              gradient="accentBackground"
            />
            <Text
              variant="body-m"
              gradient="darkSurfaceAccent"
              locNS="account"
              locKey="cancelWithdrawalCta"
            />
            <PressableOverlay onPress={cancelWithdrawal} />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

export default memo(TransactionHistoryListItem);
